import { ValueGetterFunc } from "@ag-grid-community/core";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
} from "@chakra-ui/react";
import Decimal from "decimal.js";
import { RefreshCellsOnInputChange } from "features/credit-facility/expenses/adjustments-table/utils";
import { EdgeOverrideAmount } from "features/fund-family/setup/fund-structure/hooks/useAllocationRuleGraphForm";
import { useCustomAllocationRuleGraphInputTableDataRetrieveSuspense } from "gen/endpoints/allocation-rule-graph-table/allocation-rule-graph-table";
import { GenericTableCell } from "gen/model/genericTableCell";
import { GenericTableRow } from "gen/model/genericTableRow";
import { PercentTableCellValue } from "gen/model/percentTableCellValue";
import { keys, values } from "lodash-es";
import { useCallback, useMemo } from "react";
import { DefaultValues, FieldValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import Form from "shared/components/form/Form";
import { FormFooterActions } from "shared/components/form/FormFooter";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { useToast } from "shared/components/toast/useToast";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { decimalOrZero, decimalZero } from "shared/utils/DecimalUtils";

export const CustomAllocationRuleInputModal = <FormValues extends FieldValues>({
  isOpen,
  onModalClose,
  onSubmit,
  edgeOverrideAmounts,
}: {
  isOpen: boolean;
  onModalClose: () => void;
  onSubmit: (overrides: EdgeOverrideAmount[]) => Promise<void>;
  edgeOverrideAmounts?: EdgeOverrideAmount[];
}) => {
  const { t } = useTranslation(["allocation"]);
  const { fundFamilyId } = useRequiredStringParams();
  const { showToast } = useToast();

  const existingEdgeWeightOverridesMap = useMemo(
    () =>
      edgeOverrideAmounts?.reduce(
        (acc, { securityOwnershipId, overrideAmount }) => {
          acc[securityOwnershipId] = overrideAmount;
          return acc;
        },
        {} as Record<string, string>,
      ),
    [edgeOverrideAmounts],
  );

  const form = useForm<FormValues>({
    defaultValues: existingEdgeWeightOverridesMap as DefaultValues<FormValues>,
  });

  const { data: tableData } =
    useCustomAllocationRuleGraphInputTableDataRetrieveSuspense(fundFamilyId);

  const onCancel = () => {
    onModalClose();
  };

  const onSave = async () => {
    const valuesMap = form.getValues();
    const areAnyUndefined = keys(form.getValues()).some(
      (id) => valuesMap[id] === undefined,
    );

    if (areAnyUndefined) {
      showToast({
        message: t("allocation:customRule.notAllDefinedError"),
        variant: "error",
      });
      return;
    }

    await onSubmit(
      keys(form.getValues()).map((id) => ({
        overrideAmount: valuesMap[id],
        securityOwnershipId: id,
      })),
    );
    onModalClose();
  };

  const valueGetter = useCallback<
    ValueGetterFunc<GenericTableRow, GenericTableCell>
  >(
    (params) => {
      const formValues = form.getValues();
      const colId = params.column.getColId();
      const columnData = params.node?.data;
      if (!columnData) {
        return;
      }
      if (colId === "allocation_override_percentage") {
        const thisRowsValue = decimalOrZero(formValues[columnData.id]);
        const totalOfAllValues = values(formValues).reduce(
          (a: Decimal, b) => (b ? a.plus(b) : a.plus(decimalZero())),
          decimalZero(),
        );

        if (totalOfAllValues.isZero()) {
          return {
            value: {
              type: "percent",
              value: decimalZero().toString(),
            } as PercentTableCellValue,
          };
        }
        return {
          value: {
            type: "percent",
            value: thisRowsValue.div(totalOfAllValues).times(100).toString(),
          } as PercentTableCellValue,
        } as GenericTableCell;
      }
      return;
    },
    [form],
  );

  return (
    <Form
      form={form}
      disableNavBlocker={true}
      onSubmit={async () => await onSave()}
    >
      <Modal isOpen={isOpen} size="full" onClose={onModalClose}>
        <ModalContent backgroundColor="gray.100">
          <ModalBody background="white" display="flex" flexDirection="column">
            <TableBox isFullHeight={true} isFullWidth={true}>
              <TableBoxHeader>
                <TableBoxTitle>
                  {t("allocation:customRule.title")}
                </TableBoxTitle>
                <DataTableBoxActions
                  showColControl={false}
                  showFullScreen={false}
                  showClearConfig={{
                    display: true,
                    onClear: () => {},
                  }}
                />
              </TableBoxHeader>
              <TableBoxBody>
                <RefreshCellsOnInputChange form={form}>
                  <DataTable
                    data={tableData}
                    valueGetter={valueGetter}
                    suppressStatusBar={true}
                  />
                </RefreshCellsOnInputChange>
              </TableBoxBody>
            </TableBox>
          </ModalBody>
          <ModalFooter
            pt="0px"
            pb="0px"
            backgroundColor="white"
            borderTop="1px"
            borderTopColor="gray.200"
          >
            <FormFooterActions>
              <FormCancelButton onClick={onCancel} />
              <Button
                colorScheme="primary"
                onClick={async () => {
                  await onSave();
                }}
              >
                {t("allocation:customRule.save")}
              </Button>
            </FormFooterActions>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Form>
  );
};
